/**
 * layoutModel — pure, DOM-free helpers for the configurable dashboard assembly
 * (owner directive #5050 §2). Reorder, width cycling, and widget-shape
 * normalization live here so the editing interactions are unit-tested without
 * rendering.
 */

import type { dashboard } from '@cwos/api-client';

export type WidgetWidth = 'full' | 'half' | 'third';

export interface DraftWidget {
  metricKey: string;
  visualization: dashboard.WidgetVisualization;
  width: WidgetWidth;
  config: { threshold?: number };
}

const WIDTH_CYCLE: Record<WidgetWidth, WidgetWidth> = {
  full: 'half',
  half: 'third',
  third: 'full',
};

/** Cycle a widget's width full → half → third → full. */
export function cycleWidth(width: WidgetWidth): WidgetWidth {
  return WIDTH_CYCLE[width] ?? 'full';
}

/** Swap a list item with its neighbour; no-op when the move would leave the list. */
export function moveItem<T>(list: readonly T[], index: number, direction: 'up' | 'down'): T[] {
  if (index < 0 || index >= list.length) return [...list];
  const target = direction === 'up' ? index - 1 : index + 1;
  if (target < 0 || target >= list.length) return [...list];
  const out = [...list];
  const tmp = out[index]!;
  out[index] = out[target]!;
  out[target] = tmp;
  return out;
}

/** Convert a draft widget into the API's wire shape (position is implicit). */
export function toWireWidget(widget: DraftWidget): {
  metricKey: string;
  visualization: dashboard.WidgetVisualization;
  width: WidgetWidth;
  config: { threshold?: number };
} {
  return {
    metricKey: widget.metricKey,
    visualization: widget.visualization,
    width: widget.width,
    config: widget.config.threshold !== undefined ? { threshold: widget.config.threshold } : {},
  };
}
